<template>
    <div id="app" class="app">
        <section ref="core"
                 class="app__core">
            <div class="app__header-container">
                <router-view name="header" />
            </div>
            <div v-if="renderContent" ref="container" class="app__container">
                <service-down v-if="serviceDown" :contact-email="serviceEmail" />
                <router-view v-else />
                <app-mask />
                <app-modal />
            </div>
            <div v-else class="app__container">
                <section class="flow__panel">
                    <loading-animation />
                </section>
            </div>
            <app-footer />
        </section>
        <section ref="support" class="app__support">
            <h1>Plan Builder browser support</h1>
            <p>
                We have detected that your current web browser does not meet the requirements for running the Plan Builder app.
            </p>
            <p>
                In order to give you the best possible experience we recommend you access the app using the latest version of one of the
                following modern browsers&hellip;
            </p>
            <ul>
                <li>Google Chrome</li>
                <li>Microsoft Edge</li>
                <li>Safari (MacOS / iOS only)</li>
                <li>Firefox</li>
            </ul>
            <p>
                The Plan Builder is supported on devices and platforms as follows&hellip;
            </p>
            <ul>
                <li>All Windows (with Chrome, Edge or Firefox)</li>
                <li>All MacOS X (with Chrome, Edge or Firefox)</li>
                <li>MacOS X El Capitan and above (with Safari, Chrome, Edge or Firefox)</li>
                <li>iOS 11 and above (iPhone 5s and above)</li>
                <li>Android 4.3 and above (with Chrome)</li>
            </ul>
        </section>
    </div>
</template>

<script>

import _ from 'lodash'
import Window from 'window-wrapper'

import AuthManager from '@/auth/auth-manager'

import { MUTATIONS } from '@/store/mutations'

import AppFooter from '@/components/app-core/app-footer'
import AppMask from '@/components/app-core/app-mask'
import AppModal from '@/components/app-core/app-modal'
import ServiceDown from '@/components/app-core/service-down'

import AnalyticsLogger from '@/components/analytics-logger'

import ServiceFactory from '@/services/service-factory'
import Storage from '@/storage'
import strings from '@/helpers/strings'
import SchemeServicingManager from '@/scheme-servicing-manager'

import LoadingAnimation from '@/components/app-core/loading-animation'

const LOG_NAME = 'Application'

export default {

    mixins: [
        AnalyticsLogger,
    ],

    computed: {

        /**
        * True if the child components should be rendered.
        * @returns {boolean}
        */
        renderContent() {
            return !!this.$store.state.scheme
        },
    },

    data() {
        return {
            pageTypeModifier: '',
            closing: false,
            serviceDown: false,
            serviceEmail: '',
        }
    },

    beforeDestroy() {
        // TODO: Keith Needs to update npm package WindowWrapper - He fixed this at Christmas!
        Window.removeEventListener('beforeunload', this.onReload)

        if (!this.closing) {
            this.trackEvent('application', 'app_close')
            this.closing = true
        }
    },

    async beforeMount() {

        // TODO: Keith Needs to update npm package WindowWrapper - He fixed this at Christmas!
        Window.addEventListener('beforeunload', this.onReload)

        await this.$nextTick(async() => {
            try {
                const testService = ServiceFactory.test()
                const operationState = await testService.loadServiceDiagnosticsAsync(this.$scheme.id)

                if (operationState.error)
                    operationState.throw()

                if (operationState.data.calcsDown) {
                    this.$logger.log('Calculations service is down', LOG_NAME)
                    this.$store.commit(MUTATIONS.setServiceDown, true)
                    this.serviceDown = true
                    this.serviceEmail = operationState.data.schemeEmailAddress
                }
            } catch (err) {
                this.$logger.error(`Service error: ${ err }`, LOG_NAME)
                this.serviceDown = true
            }

            this._setSchemeReferences()
            await this._loadPartnerLink()
            this._bindGlobalEvents()

            if (!this.$router.staticRoutes.includes(this.$router.currentRoute.name))
                await AuthManager.startAtAppropriateRouteForStateAsync()
            else
                this.$logger.log(`Static Route on load: ${ this.$router.currentRoute.name }`, LOG_NAME)
        })
    },

    created() {
        if (window)
            window.addEventListener('resize', () => this.$store.commit(MUTATIONS.setWindowWidth))
    },

    mounted() {
        this._accountForOldIOS()

        if (document.referrer) {
            this.trackEvent('application', 'app_load', { additional: null, label: null, value: document.referrer })
            this.trackEvent('application', 'app_load', { additional: document.referrer, label: null, value: null })
        } else {
            this.trackEvent('application', 'app_load')
            this.trackEvent('application', 'app_load', { additional: 'direct', label: null, value: null })
        }
    },

    unmounted() {
        this._clearGlobalEvents()

        if (!this.closing) {
            this.trackEvent('application', 'app_close')
            this.closing = true
        }
    },

    methods: {

        onReload($event) {
            $event.preventDefault()
            $event.returnValue = ''
        },

        /**
         * Load partner link data and filter services based on querystring.
         */
        async _loadPartnerLink() {
            this.$logger.log('Loading Partner Referral Identity', 'Referrals')

            // load from session storage if it's available
            let partRef = await Storage.session.getPartnerReferralAsync()
            this.$logger.logObject(partRef, 'Partner Referral Identity in Storage', 'Referrals')

            // if partRef is available in the querystring use that as a preference
            if (location.search.length > 0)
                partRef = strings.query(location.search.substring(1), this.$config.partnerReferralKey)
            this.$logger.logObject(partRef, 'Partner Referral Identity from QueryString', 'Referrals')

            // if partRef is available from any source, load the partner and update the scheme services
            if (partRef) {
                const partRefValue = parseInt(partRef)
                await SchemeServicingManager.updatePartnerServicesAsync(partRefValue)
                this.$logger.logObject(partRefValue, 'Partner Referral Identity and behaviour updated', 'Referrals')
            }
        },

        _accountForOldIOS() {
            if (/iP(hone|od|ad)/.test(navigator.platform)) {
                let block = false

                if (/OS [1-9]_(.*) like Mac OS X/i.test(navigator.userAgent))
                    block = true
                else if (/OS 10_(.*) like Mac OS X/i.test(navigator.userAgent))
                    block = true


                if (block) {
                    this.$refs.core.setAttribute('style', 'display: none;')
                    this.$refs.support.setAttribute('style', 'display: block;')
                }
            }
        },

        _bindGlobalEvents() {
            this._clearGlobalEvents()
            this.$eventbus.$on(this.$appevents.pageTyped, (modifier) => this.pageTypeModifier = modifier)
        },

        _clearGlobalEvents() {
            this.$eventbus.$off(this.$appevents.pageTyped)
        },

        _setSchemeReferences() {

            this.$store.commit(MUTATIONS.setScheme, this.$scheme.toStoreJson())
            const appTitleRef = document.getElementById('hh-app-title')
            appTitleRef.innerText = this.$scheme.title

            const removeCssRef = (ref) => {
                const refObj = document.getElementById(`js-external-font-css-${ ref }`)
                if (refObj && _.isFunction(refObj.remove)) refObj.remove()
            }

            const removeCssRefs = (refs) => {
                refs.forEach(ref => removeCssRef(ref))
            }

            const setCssRef = (ref, uri) => {
                const refObj = document.getElementById(`js-external-font-css-${ ref }`)
                refObj.setAttribute('href', uri)
            }

            const setCssRefs = (uris) => {
                for (let i = 0; i < uris.length; i++)
                    setCssRef(`${ i+1 }`, uris[i])
            }

            if (this.$scheme.fontCss) {
                if (Array.isArray(this.$scheme.fontCss)) {
                    setCssRefs(this.$scheme.fontCss)
                    if (this.$scheme.fontCss.length < 4)
                        removeCssRefs(this.$scheme.fontCss.length === 3 ? [ '4' ] : this.$scheme.fontCss.length === 2 ? [ '3', '4' ] : [ '2', '3', '4' ])
                } else {
                    setCssRef('1', this.$scheme.fontCss)
                    removeCssRefs([ '2', '3', '4' ])
                }
            } else {
                removeCssRefs([ '1', '2', '3', '4' ])
            }

            if (document) {
                const appIcons = document.getElementsByClassName('hh-app-icon')
                for (let i = 0; i < appIcons.length; i++) {
                    const fileRef = appIcons[i].getAttribute('href')
                    appIcons[i].setAttribute('href', `${ this.$config.baseUrl }schemes/${ this.$scheme.path }${ fileRef }`)
                }

                const appIconsWithContent = document.getElementsByClassName('hh-app-icon-content')
                for (let i = 0; i < appIconsWithContent.length; i++) {
                    const fileRef = appIconsWithContent[i].getAttribute('content')
                    appIconsWithContent[i].setAttribute('content', `${ this.$config.baseUrl }schemes/${ this.$scheme.path }${ fileRef }`)
                }

                const metas = document.getElementsByTagName('meta')
                for (let i = 0; i < metas.length; i++) {
                    if (metas[i].getAttribute('name') === 'theme-color')
                        metas[i].setAttribute('content', this.$scheme.themeColor || '#FFFFFF')
                    else if (metas[i].getAttribute('name') === 'msapplication-TileColor')
                        metas[i].setAttribute('content', this.$scheme.tileColor || this.$scheme.themeColor || '#FFFFFF')
                }
            }
        },
    },

    components: {
        AppFooter,
        AppMask,
        AppModal,
        ServiceDown,
        LoadingAnimation,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\views\_app.scss

.app {
    position: relative;

    @include element('support') {
        background-color: transparent;
        max-width: 100%;
        padding: 10px 10px 0 10px;

        @include bp-min-small {
            padding: 0.5em 1em;
            margin: rem(40px) rem(20px) rem(20px) rem(20px);
            min-height: $inner-wizard-container-min-height;
        }

        @include bp-min-mid {
            max-width: rem(800px);
            margin-left: auto;
            margin-right: auto;
            padding: 1em 1.2em;
        }
    }

    @include element('container') {
        padding-top: header-height('base');

        @include bp-min-small {
            padding-top: header-height('small');
        }
        @include bp-min-mid {
            padding-top: header-height('mid');
        }
        @include bp-min-wide {
            padding-top: header-height('wide');
        }

        z-index: z-index('base');
    }

    @include element('header-container') {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: z-index('header');
    }

    @include element('content') {

        @include bp-min-wide {
            // noinspection CssInvalidFunction
            width: span(8);
        }
    }

    @include element('aside') {
        // noinspection CssInvalidFunction
        width: span(12);

        @include bp-min-wide {
            // noinspection CssInvalidFunction
            width: span(4 of 12);

            @include modifier('left-margin') {
                // noinspection CssInvalidFunction
                margin-left: gutter();
            }

            @include modifier('right-margin') {
                // noinspection CssInvalidFunction
                margin-right: gutter();
            }
        }
    }
}

</style>
